import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/Select';
import { formatDate } from '../../lib/utils';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/api';
import { 
  MessageSquare, 
  Send, 
  X, 
  User, 
  Clock, 
  Tag, 
  AlertCircle,
  CheckCircle,
  XCircle,
  Edit,
  UserPlus
} from 'lucide-react';

// Importar tipos compartilhados
import { Ticket } from '../../types/ticket';
import { useAuthStore } from '../../store/authStore';

interface TicketViewModalProps {
  ticket: Ticket;
  isOpen: boolean;
  onClose: () => void;
  onTicketUpdate: () => void;
}

const TicketViewModal: React.FC<TicketViewModalProps> = ({
  ticket,
  isOpen,
  onClose,
  onTicketUpdate
}) => {
  const { user, canCloseTickets, canDeleteTickets, isAdmin } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ticketData, setTicketData] = useState<Ticket>(ticket);
  const [statusFilter, setStatusFilter] = useState<string>(ticket.status);

  const fetchTicketDetails = useCallback(async () => {
    try {

      const data = await apiService.getTicket(ticket._id);
      const ticketInfo = data.data;

      // Processar dados do ticket para garantir que createdBy e sentBy estejam preenchidos
      const processedTicket = {
        ...ticketInfo,
        createdBy: ticketInfo.createdBy || {
          _id: user?.id || 'unknown',
          username: user?.username || 'Usuário Desconhecido',
          email: user?.email || 'n/a',
          name: user?.name || user?.username || 'Usuário Desconhecido',
          role: user?.level || 'user'
        },
        messages: (ticketInfo.messages || []).map((message: any) => ({
          ...message,
          sentBy: message.sentBy || {
            _id: user?.id || 'unknown',
            username: user?.username || 'Usuário Desconhecido',
            email: user?.email || 'n/a',
            name: user?.name || user?.username || 'Usuário Desconhecido',
            role: user?.level || 'user'
          }
        }))
      };
      
      setTicketData(processedTicket);
      setStatusFilter(processedTicket.status);
    } catch (error) {
      console.error('Erro ao carregar detalhes do ticket:', error);
    }
  }, [ticket._id, user?.id, user?.username, user?.email, user?.name, user?.level]);

  // Carregar dados atualizados do ticket
  useEffect(() => {
    if (isOpen && ticket._id) {
      fetchTicketDetails();
    }
  }, [isOpen, ticket._id, fetchTicketDetails]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setIsLoading(true);
      
      await apiService.addTicketMessage(ticket._id, {
        content: newMessage,
        isInternal: isInternal,
        sentBy: {
          _id: user?.id,
          username: user?.username,
          email: user?.email,
          name: user?.name || user?.username,
          role: user?.level || 'user'
        }
      });

      setNewMessage('');
      setIsInternal(false);
      await fetchTicketDetails();
      onTicketUpdate();
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      setIsLoading(true);
      
      await apiService.updateTicketStatus(ticket._id, newStatus, newStatus === 'closed' ? 'admin_closed' : undefined);

      setStatusFilter(newStatus as any);
      await fetchTicketDetails();
      onTicketUpdate();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Função para encerrar ticket
  const handleCloseTicket = async () => {
    if (!canCloseTickets()) {
      alert('Você não tem permissão para encerrar tickets');
      return;
    }

    if (!window.confirm('Tem certeza que deseja encerrar este ticket?')) {
      return;
    }

    try {
      setIsLoading(true);
      await apiService.updateTicketStatus(ticket._id, 'closed', 'admin_closed');
      
      await fetchTicketDetails();
      onTicketUpdate();
      alert('Ticket encerrado com sucesso!');
    } catch (error) {
      console.error('Erro ao encerrar ticket:', error);
      alert('Erro ao encerrar ticket');
    } finally {
      setIsLoading(false);
    }
  };

  // Função para deletar ticket
  const handleDeleteTicket = async () => {
    if (!canDeleteTickets()) {
      alert('Você não tem permissão para deletar tickets');
      return;
    }

    if (!window.confirm('Tem certeza que deseja deletar este ticket? Esta ação não pode ser desfeita!')) {
      return;
    }

    try {
      setIsLoading(true);
      const token = useAuthStore.getState().token;
      const response = await fetch(`/api/tickets/${ticket._id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        onTicketUpdate();
        onClose();
        alert('Ticket deletado com sucesso!');
      } else {
        console.error('Erro ao deletar ticket:', response.statusText);
        alert('Erro ao deletar ticket');
      }
    } catch (error) {
      console.error('Erro ao deletar ticket:', error);
      alert('Erro ao deletar ticket');
    } finally {
      setIsLoading(false);
    }
  };

  // Função para atribuir ticket
  const handleAssignTicket = async (adminId: string) => {
    try {
      setIsLoading(true);
      const token = useAuthStore.getState().token;
      const response = await fetch(`/api/tickets/${ticket._id}/assign`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ adminId }),
      });

      if (response.ok) {
        await fetchTicketDetails();
        onTicketUpdate();
        alert('Ticket atribuído com sucesso!');
      } else {
        console.error('Erro ao atribuir ticket:', response.statusText);
        alert('Erro ao atribuir ticket');
      }
    } catch (error) {
      console.error('Erro ao atribuir ticket:', error);
      alert('Erro ao atribuir ticket');
    } finally {
      setIsLoading(false);
    }
  };

  // Função para obter ícone do status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <Edit className="h-4 w-4" />;
      case 'waiting_response': return <MessageSquare className="h-4 w-4" />;
      case 'closed': return <XCircle className="h-4 w-4" />;
      case 'resolved': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  // Função para obter cor do status
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-blue-600 bg-blue-100';
      case 'in_progress': return 'text-yellow-600 bg-yellow-100';
      case 'waiting_response': return 'text-orange-600 bg-orange-100';
      case 'closed': return 'text-gray-600 bg-gray-100';
      case 'resolved': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Função para obter cor da prioridade
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-blue-600 bg-blue-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'urgent': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Função para obter label do status
  const getStatusLabel = (status: string) => {
    const labels = {
      open: 'Aberto',
      in_progress: 'Em Andamento',
      waiting_response: 'Aguardando Resposta',
      closed: 'Fechado',
      resolved: 'Resolvido'
    };
    return labels[status as keyof typeof labels] || status;
  };

  // Função para obter label da prioridade
  const getPriorityLabel = (priority: string) => {
    const labels = {
      low: 'Baixa',
      medium: 'Média',
      high: 'Alta',
      urgent: 'Urgente'
    };
    return labels[priority as keyof typeof labels] || priority;
  };

  // Função para obter label da categoria
  const getCategoryLabel = (category: string) => {
    const labels = {
      technical: 'Técnico',
      billing: 'Faturamento',
      account: 'Conta',
      feature_request: 'Solicitação de Funcionalidade',
      bug_report: 'Relatório de Bug',
      other: 'Outro'
    };
    return labels[category as keyof typeof labels] || category;
  };

  // Função para obter ícone do tipo de mensagem
  const getMessageIcon = (type: string) => {
    switch (type) {
      case 'user_message': return <User className="h-4 w-4" />;
      case 'admin_response': return <MessageSquare className="h-4 w-4" />;
      case 'system_note': return <AlertCircle className="h-4 w-4" />;
      case 'status_change': return <Edit className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  // Função para obter cor do tipo de mensagem
  const getMessageColor = (type: string) => {
    switch (type) {
      case 'user_message': return 'bg-blue-50 border-blue-200';
      case 'admin_response': return 'bg-green-50 border-green-200';
      case 'system_note': return 'bg-yellow-50 border-yellow-200';
      case 'status_change': return 'bg-purple-50 border-purple-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-2 sm:p-4">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between text-lg sm:text-xl">
            <span className="truncate pr-2">{ticketData.title}</span>
            <Button variant="outline" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6">
          {/* Informações do Ticket */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
font-bold   <div className="space-y-2">
              <label className="text-lg font-bold text-gray-700">Status</label>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-lg font-medium ${getStatusColor(ticketData.status)}`}>
                  {getStatusIcon(ticketData.status)}
                  {getStatusLabel(ticketData.status)}
                </span>
                {user?.role === 'admin' && (
                  <Select value={statusFilter} onValueChange={handleStatusChange}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Aberto</SelectItem>
                      <SelectItem value="in_progress">Em Andamento</SelectItem>
                      <SelectItem value="waiting_response">Aguardando Resposta</SelectItem>
                      <SelectItem value="resolved">Resolvido</SelectItem>
                      <SelectItem value="closed">Fechado</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-lg mr-2 font-bold text-gray-700">Prioridade</label>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-lg font-medium ${getPriorityColor(ticketData.priority)}`}>
                {getPriorityLabel(ticketData.priority)}
              </span>
            </div>

            <div className="space-y-2">
              <label className="text-lg mr-2 font-bold text-gray-700">Categoria</label>
              <span className="text-lg text-gray-900">{getCategoryLabel(ticketData.category)}</span>
            </div>
          </div>

          {/* Tags */}
          {ticketData.tags.length > 0 && (
            <div className="space-y-2">
              <label className="text-lg font-medium text-gray-700">Tags</label>
              <div className="flex flex-wrap gap-2">
                {ticketData.tags.map((tag, index) => (
                  <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-lg bg-gray-100 text-gray-800">
                    <Tag className="h-3 w-3 mr-1" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Informações do Usuário */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label className="text-lg font-semibold text-gray-700">Criado por</label>
              {ticketData.createdBy ? (
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{ticketData.createdBy.name}</p>
                    <p className="text-sm text-gray-600 truncate">@{ticketData.createdBy.username}</p>
                    <p className="text-xs text-blue-600 font-medium capitalize">{ticketData.createdBy.role}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Usuário não encontrado</span>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-lg font-semibold text-gray-700">Atribuído a</label>
                {isAdmin() && (
                  <Button
                    onClick={() => handleAssignTicket(user?.id || '')}
                    disabled={isLoading}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    <UserPlus className="h-3 w-3 mr-1" />
                    Atribuir a mim
                  </Button>
                )}
              </div>
              {ticketData.assignedTo ? (
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{ticketData.assignedTo.name}</p>
                    <p className="text-sm text-gray-600 truncate">@{ticketData.assignedTo.username}</p>
                    <p className="text-xs text-green-600 font-medium capitalize">{ticketData.assignedTo.role}</p>
                  </div>
                  {isAdmin() && (
                    <Button
                      onClick={() => handleAssignTicket('')}
                      disabled={isLoading}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="flex-1">
                    <span className="text-gray-500 text-sm font-medium">Não atribuído</span>
                    <p className="text-xs text-gray-400">Aguardando atribuição</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <label className="text-lg font-medium text-gray-700">Descrição</label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-lg text-gray-900 whitespace-pre-wrap">{ticketData.description}</p>
            </div>
          </div>

          {/* Mensagens */}
          <div className="space-y-4">
            <label className="text-lg font-medium text-gray-700">Conversa</label>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {ticketData.messages?.map((message) => (
                <div
                  key={message._id}
                  className={`p-3 rounded-lg border ${getMessageColor(message.type)}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        {getMessageIcon(message.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        {message.sentBy ? (
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900 truncate">
                              {message.sentBy.name}
                            </span>
                            <span className="text-sm text-gray-500 truncate">
                              @{message.sentBy.username}
                            </span>
                            <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full capitalize">
                              {message.sentBy.role}
                            </span>
                          </div>
                        ) : (
                          <span className="font-medium text-gray-500">
                            Usuário não encontrado
                          </span>
                        )}
                        {message.isInternal && (
                          <span className="inline-block mt-1 text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                            Mensagem Interna
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(message.createdAt)}
                    </span>
                  </div>
                  <p className="text-lg text-gray-900 whitespace-pre-wrap">
                    {message.content}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Nova Mensagem */}
          <div className="space-y-4">
            <label className="text-lg font-medium text-gray-700">Responder</label>
            <div className="space-y-3">
              <Textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Digite sua mensagem..."
                rows={3}
              />
              
              {isAdmin() && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isInternal"
                    checked={isInternal}
                    onChange={(e) => setIsInternal(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="isInternal" className="text-lg text-gray-700">
                    Mensagem interna (só administradores veem)
                  </label>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || isLoading}
                  className="flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  {isLoading ? 'Enviando...' : 'Enviar'}
                </Button>
              </div>
            </div>
          </div>

          {/* Ações de Administrador */}
          {(canCloseTickets() || canDeleteTickets()) && (
            <div className="border-t pt-4 mt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-700">Ações de Administrador</h3>
                <div className="flex gap-2">
                  {canCloseTickets() && ticketData.status !== 'closed' && ticketData.status !== 'resolved' && (
                    <Button
                      onClick={handleCloseTicket}
                      disabled={isLoading}
                      variant="outline"
                      className="flex items-center gap-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                    >
                      <XCircle className="h-4 w-4" />
                      Encerrar Ticket
                    </Button>
                  )}
                  
                  {canDeleteTickets() && (
                    <Button
                      onClick={handleDeleteTicket}
                      disabled={isLoading}
                      variant="outline"
                      className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="h-4 w-4" />
                      Deletar Ticket
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TicketViewModal;
